/**
 * <cart-line-toggle> — switches a cart line between subscription and
 * one-time purchase, and repairs a line whose plan encodes the wrong tier
 * for its own quantity.
 *
 * ⚠️ The mutation addresses the line by KEY (`id`), never by 1-based `line`
 * index. An older comment here asserted that /cart/change.js accepts
 * `selling_plan` only alongside `line` — that is FALSE, verified against the
 * live cart: `{id: <line key>, quantity, selling_plan}` switches the plan
 * correctly. The distinction is not cosmetic. The gift reconciler inserts and
 * removes lines asynchronously, so an index resolved from a /cart.js read is
 * stale the moment a gift lands, and the mutation then hits WHATEVER LINE now
 * occupies that slot. That is not theoretical: it put a free gift line onto a
 * 6-weekly subscription and charged $31.49 for it. Re-reading the cart
 * "immediately before" the POST does not close the window — only addressing
 * the line by something that survives reordering does.
 *
 * `quantity` must still be sent (omitting it resets the line to 1), and it is
 * read from a fresh /cart.js so it is the server's number rather than a
 * possibly stale render. A shifted index no longer matters to that read.
 *
 * The response's sections are applied via the shared cart section utils and
 * published with source 'cart-items' (Dawn's "already rendered" signal); the
 * <cart-rewards> reconciler reacts to the publish and re-evaluates gifts.
 *
 * Every plan id this file posts was resolved in Liquid. It reads two data
 * attributes and never decides which plan is correct.
 */

/**
 * Tier repairs currently IN FLIGHT, keyed by line key + target plan.
 *
 * This is the ONLY guard, and it exists for exactly one reason: both cart
 * surfaces render the same line (the drawer is on every template, so /cart has
 * it twice), so two elements connect for it in the same tick and would both
 * fire. The first claims the key before its first await, the second drops out.
 * Released when the repair settles.
 *
 * ⚠️ It must NOT be made permanent, and no per-variant quota belongs beside it.
 * Both have now been tried and both were money bugs:
 *
 *   - A permanent `variant|plan` stamp skipped the SECOND time a session earned
 *     the same repair (qty 3 → 1, 1 → 3, 3 → 1 — the third is a real repair the
 *     stamp had already spent), leaving the line on the wrong plan.
 *   - A 12-per-variant budget did the same thing with a longer fuse. The live
 *     planMap tiers are 1/2/3, so every stepper click crosses a tier boundary
 *     and three up-and-down sweeps exhaust it — ordinary behaviour for a
 *     shopper comparing pack sizes.
 *
 * A quota is the wrong shape anyway, because a legitimate repeat is identical
 * to a loop in every observable: same variant, same target plan, same quantity.
 * What separates them is an intervening user action, which is state we do not
 * have — hence in-flight only.
 *
 * Nothing bounds the repair count, and nothing needs to. A merge cannot
 * ping-pong: `changePlan` posts the line's own current server quantity, so our
 * writes never DECREASE it, and the tier is monotone in quantity — a merge
 * chain therefore walks strictly up the ladder and terminates at the top tier.
 * The remaining non-convergence (one plan id appearing in two tier rows) is
 * refused server-side; see the round-trip check in custom-cart-line-toggle.liquid.
 */
const cartTierRepairsInFlight = new Set();

if (!customElements.get('cart-line-toggle')) {
  customElements.define(
    'cart-line-toggle',
    class CartLineToggle extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
        this.repairTierDrift();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        // Release the busy count the swap is about to make unreleasable: on
        // SUCCESS this element is replaced while still busy, so setBusy(false)
        // never runs and the flag would stick on <html> for the rest of the
        // session, leaving every price permanently dimmed.
        if (this.isBusy) {
          this.isBusy = false;
          setCartBusy(false);
        }
      }

      /**
       * The control is a one-way action, not a two-state pill: the line is
       * already in one of the two states, so the server renders only the way
       * out of it (a "Subscribe & save another n%" CTA, or a quiet "Switch to
       * one-time" link). There is therefore nothing to flip optimistically —
       * the new label is a percentage only Liquid can compute, and inventing
       * customer-facing text in JS is against the theme's rules. The click
       * simply disables the control and marks the derived values stale until
       * the section swap lands.
       *
       * The control still does not DIM: it is what the shopper just touched,
       * and greying their own input a beat after they use it is what makes an
       * interface feel laggy. Only what the server has yet to recompute (this
       * line's price, the totals, the rewards rail) goes stale.
       *
       * setBusy(false) only ever runs on failure paths — on success this
       * element is disconnected by the swap before finally executes, so the
       * busy count is released by the unload rather than here. That is why
       * setCartBusy is counted: see custom-cart-section-utils.js.
       */
      setBusy(busy) {
        this.querySelectorAll('[data-toggle-type]').forEach((b) => {
          b.disabled = busy;
        });
        const row = this.closest('.cart-item');
        row?.classList.toggle('cart-item--switching', busy);
        row?.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.toggle('hidden', !busy));
        if (busy !== this.isBusy) {
          this.isBusy = busy;
          setCartBusy(busy);
        }
      }

      async onClick(event) {
        const button = event.target.closest('[data-toggle-type]');
        if (!button || button.disabled) return;
        const targetType = button.dataset.toggleType;
        const hasPlan = this.dataset.hasPlan === 'true';
        if ((targetType === 'subscribe') === hasPlan) return;

        await this.changePlan(targetType === 'subscribe' ? Number(this.dataset.subscribePlanId) : '', true);
      }

      /**
       * Put this line back on the plan whose tier matches its own quantity.
       *
       * Liquid decides whether there is drift and what the replacement is (see
       * the tier drift guard in custom-cart-line-toggle.liquid) — the presence
       * of the attribute IS the instruction. It fires on connect rather than on
       * a quantity event because every quantity change re-renders the section,
       * so the freshly rendered line always carries a current verdict, however
       * the quantity moved: Dawn's stepper, a popover, or another tab.
       *
       * No live-region announcement: the shopper did not ask for this and the
       * price moving under a screen reader mid-render is worse than silent
       * correctness. The visible price and the "N% Off" badge tell the truth
       * either way.
       */
      async repairTierDrift() {
        const planId = this.dataset.correctPlanId;
        if (!planId) return;
        const lineKey = this.dataset.lineKey ?? '';
        const claim = `${lineKey}|${planId}`;
        if (cartTierRepairsInFlight.has(claim)) return;

        // Claimed BEFORE the first await: both surfaces render this line, so
        // two elements connect for it in the same tick and would otherwise
        // both fire.
        cartTierRepairsInFlight.add(claim);
        try {
          await this.changePlan(Number(planId), false);
        } finally {
          // On success the section swap has already connected replacement
          // elements by now, and they carry a fresh (blank) verdict — so
          // releasing here cannot re-trigger this repair, only permit the next
          // one the shopper earns.
          cartTierRepairsInFlight.delete(claim);
        }
      }

      /**
       * @param {number|string} sellingPlan - plan id, or '' to drop the plan
       * @param {boolean} announce - speak the change in the cart live region
       */
      async changePlan(sellingPlan, announce) {
        this.setBusy(true);
        try {
          const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
            (response) => (response.ok ? response.json() : null),
          );
          if (!cart) return;
          const target = cart.items.find((item) => item.key === this.dataset.lineKey);
          if (!target) return; // line vanished (merged/removed) — section refresh will fix the UI

          const sectionsToRender = cartSectionsToRender();
          const response = await fetch(routes.cart_change_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              id: this.dataset.lineKey,
              quantity: target.quantity,
              selling_plan: sellingPlan,
              sections: sectionsToRender.map((entry) => entry.section),
              sections_url: window.location.pathname,
            }),
          });
          if (!response.ok) {
            console.warn('[cart-line-toggle] plan change failed:', response.status);
            return;
          }
          const state = await response.json();
          if (state.sections) renderCartSections(state.sections, sectionsToRender);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
          }
          const region =
            announce &&
            (document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText'));
          if (region) {
            region.setAttribute('aria-hidden', 'false');
            region.textContent = this.dataset.textSwitched ?? '';
            setTimeout(() => region.setAttribute('aria-hidden', 'true'), 1000);
          }
        } catch (error) {
          console.warn('[cart-line-toggle] error:', error);
        } finally {
          // Give the control back if the element is still in the DOM (i.e. no
          // section swap happened — the request failed). Its label is still
          // truthful: the line never changed state.
          if (this.isConnected) this.setBusy(false);
        }
      }
    },
  );
}
